import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X } from "lucide-react"; // Import Lucide React icon
import { LoginImg2, logo } from "../../assets/img/index";
import { Button, InputField } from "@/components";
import { useApi } from "@/";
import { useFieldError, useValidation } from "@/";
import { LoginSchema } from "../../schemas";

const Login = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const navigate = useNavigate();
  const [generalError, setGeneralError] = useState("");
  const { callApi } = useApi();
  const { validate } = useValidation(LoginSchema);
  const [formError, setFormError] = useState([]);
  const getErrorMessage = useFieldError(formError);

  // Handle input changes
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError("");

    // Validate the form data
    const result = validate(formData);
    console.log("Validation result:", result);
    if (!result.isValid) {
      setFormError(result.errors);
      return;
    }

    try {
      // Call the API to register the user
      const response = await callApi("admin/login", "POST", result.data);

      // Check if the registration was successful
      if (response.success) {
        const now = new Date();
        const expiryDate = new Date(now.getTime() + 24 * 60 * 60 * 1000);

        const token = response.data.token;
        const refreshToken = response.data.refreshToken;
        localStorage.setItem("token", token, { expires: expiryDate });
        localStorage.setItem("refreshToken", refreshToken, {
          expires: expiryDate,
        });
        navigate("/"); // Redirect to dashboard on success
      } else {
        // Display API-specific error message
        console.log("Login failed:", response);
        setFormError([]);
        setGeneralError(response.error || "Login failed. Please try again.");
      }
    } catch (error) {
      // Handle unexpected errors (e.g., network issues)
      console.error("Unexpected error during login:", error);
      setGeneralError(
        error.message || "Something went wrong. Please try again."
      );
    }
  };

  const handleClose = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setGeneralError("You are not logged in, please log-in first.");
      return;
    }
    navigate("/");
  };

  return (
    <div className="relative flex w-full h-dvh">
      {/* Close Button */}
      <button
        className="absolute top-4 right-4 p-2 z-10 rounded-xl bg-background_2/60 hover:bg-primary/30 transition-all"
        onClick={handleClose}
      >
        <X className="w-5 h-5 text-secondary/60" />
      </button>

      {/* Left Side */}
      <div className="hidden sm:flex w-1/2 flex-col justify-center items-start bg-background_2 p-2 relative">
        <img
          src={LoginImg2}
          alt="Login Visual"
          className="w-full h-full rounded-xl object-cover"
        />
      </div>

      {/* Right Side */}
      <div className="w-full h-full sm:w-1/2 flex flex-col justify-center bg-background gap-3 p-8 relative">
        <img src={logo} alt="Logo" className="absolute top-8 left-8 w-40" />
        <div className="w-full max-w-lg mb-8">
          <h2 className="text-headline3 text-secondary font-nunito font-semibold">
            Welcome Back,
          </h2>
          <p className="text-subtitle2 text-secondary/65 font-ptsans w-[70%]">
            Shipping Without Stress, From Your Place to Your Customer’s.
          </p>
        </div>
        <form
          className="w-full flex flex-col gap-2 max-w-lg"
          onSubmit={handleSubmit}
        >
          {/* General Error Message */}
          {generalError && <p className="text-red-500 mb-4">{generalError}</p>}

          {/* Email Field */}
          <InputField
            type="email"
            id="email"
            value={formData.email}
            onChange={(value) => handleChange("email", value)}
            placeholder="Enter your email"
            error={getErrorMessage("email")}
          />

          {/* Password Field */}
          <InputField
            type="password"
            id="password"
            value={formData.password}
            error={getErrorMessage("password")}
            onChange={(value) => handleChange("password", value)}
            placeholder="Enter your password"
          />

          {/* Submit Button */}
          <Button type="submit">Login</Button>
        </form>
      </div>
    </div>
  );
};

export default Login;
